<template>
  <div class="tir-pm-icon-picker">
    <TirPmPopover
      padding="0.75rem 0 0.25rem 0"
      :position="PopoverPositionEnum.Bottom"
      @open="onOpen"
      @close="onClose"
    >
      <div
        :class="['tir-pm-icon-picker__select', { 'is-selected': isSelected }]"
      >
        <div class="tir-pm-icon-picker__select-info">
          <span class="tir-pm-icon-picker__label">{{ label }}</span>
          <TirPmIconPickerViewIcon
            v-if="selectedIcon && selectedIconColor"
            :icon="selectedIcon"
            :color="selectedIconColor"
          />
        </div>
        <div
          :class="[
            'tir-pm-icon-picker__select-chevron',
            { 'is-opened': isOpened },
          ]"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M17.5 13.75L12 8.25L6.5 13.75"
              stroke="#3D424B"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
      </div>
      <template #content>
        <div
          class="tir-pm-icon-picker__popover-content"
          :style="{ gridTemplateColumns: gridTemplateColumsContent }"
        >
          <div>
            <TirPmScrollbar :style="{ maxHeight: height }" is-vertical-left>
              <div
                class="tir-pm-icon-picker__grid-list"
                :style="{
                  gridTemplateColumns: gridTemplateColumnsList,
                  paddingLeft: '0.625rem',
                }"
              >
                <div
                  v-for="icon in icons"
                  :key="icon.IconId"
                  :class="[
                    'tir-pm-icon-picker__list-item-wrap',
                    {
                      'is-selected':
                        selectedIconId && selectedIconId === icon.IconId,
                    },
                  ]"
                  @click="selectIcon(icon)"
                >
                  <TirPmIconPickerViewIcon :icon="icon" />
                </div>
              </div>
            </TirPmScrollbar>
          </div>
          <div class="tir-pm-icon-picker__popover-content-divider" />
          <div>
            <TirPmScrollbar :style="{ maxHeight: height }">
              <div
                class="tir-pm-icon-picker__grid-list"
                :style="{ gridTemplateColumns: gridTemplateColumnsList }"
              >
                <div
                  v-for="color in colors"
                  :key="color.ColorId"
                  :class="[
                    'tir-pm-icon-picker__list-item-wrap',
                    {
                      'is-selected':
                        selectedIconColorId &&
                        selectedIconColorId === color.ColorId,
                    },
                  ]"
                  @click="selectColor(color)"
                >
                  <div
                    class="tir-pm-icon-picker__color-square"
                    :style="{ backgroundColor: color.Color }"
                  />
                </div>
              </div>
            </TirPmScrollbar>
          </div>
        </div>
      </template>
    </TirPmPopover>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { TirPmPopover, PopoverPositionEnum } from "tir-pm-popover";
import { TirPmScrollbar } from "tir-pm-scrollbar";
import { IconColorType, IconType } from "./types";
import { SelectedIconModel } from "./models";
import TirPmIconPickerViewIcon from "./TirPmIconPickerViewIcon.vue";

const emit = defineEmits<{
  /**Событие выбора иконки или цвета */
  (e: "update:modelValue", value: SelectedIconModel): void;
  /**Событие выбора иконки */
  (e: "selectIcon", icon: IconType): void;
  /**Событие выбора цвета */
  (e: "selectColor", color: IconColorType): void;
}>();

const props = withDefaults(
  defineProps<{
    /**Выбранная иконка */
    modelValue?: SelectedIconModel;
    /**Список иконок */
    icons: Array<IconType>;
    /**Список цветов для иконок */
    colors: Array<IconColorType>;
    /**Кол-во колонок */
    columnsCount?: number;
    /**Кол-во строк */
    rowsCount?: number;
    /**Подсказка */
    label?: string;
  }>(),
  { columnsCount: 4, rowsCount: 5, label: "Иконка" }
);

/**ID выбранной иконки */
const selectedIconId = ref<string | undefined>(props.modelValue?.IconId);
/**ID выбранного цвета */
const selectedIconColorId = ref<string | undefined>(props.modelValue?.ColorId);
/**Выбранная иконка */
const selectedIcon = computed(() =>
  props.icons.find((icon) => icon.IconId === selectedIconId.value)
);
/**Выбранный цвет */
const selectedIconColor = computed(() =>
  props.colors.find((color) => color.ColorId === selectedIconColorId.value)
);

/**Иконка и цвет выбраны */
const isSelected = computed(
  () => selectedIcon.value && selectedIconColor.value
);

watch(
  () => props.modelValue,
  () => {
    if (selectedIconId.value !== props.modelValue?.IconId)
      selectedIconId.value = props.modelValue?.IconId;
    if (selectedIconColorId.value !== props.modelValue?.ColorId)
      selectedIconColorId.value = props.modelValue?.ColorId;
  },
  { deep: true }
);

/**Открыт ли поповер */
const isOpened = ref(false);
/**При открытии поповера */
const onOpen = () => (isOpened.value = true);
/**При закрытии попомера */
const onClose = () => (isOpened.value = false);

/**Высота для скролла */
const height = computed(
  () => `${props.rowsCount * 2 + (props.rowsCount - 0.063) * 0.5}rem`
);

const gridTemplateColumsContent = computed(() => {
  const listWidth = props.columnsCount * 2 + (props.columnsCount - 0.063) * 0.5 + 0.625;
  return `${listWidth}rem 1.063rem ${listWidth}rem`;
});

const gridTemplateColumnsList = computed(() => {
  return `repeat(${props.columnsCount}, 2rem)`;
});

const updateModelValue = () => {
  if (selectedIconId.value && selectedIconColorId.value)
    emit(
      "update:modelValue",
      new SelectedIconModel({
        IconId: selectedIconId.value,
        ColorId: selectedIconColorId.value,
      })
    );
};

const selectIcon = (icon: IconType) => {
  selectedIconId.value = icon.IconId;
  emit("selectIcon", icon);
  updateModelValue();
};

const selectColor = (color: IconColorType) => {
  selectedIconColorId.value = color.ColorId;
  emit("selectColor", color);
  updateModelValue();
};
</script>

<style lang="scss" scoped>
.tir-pm-icon-picker {
  cursor: pointer;

  &__label {
    display: block;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.25rem;
    color: var(--text-secondary, #686c73);
  }
  &__list-item-wrap {
    width: 2rem;
    height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border-radius: 0.5rem;

    &.is-selected {
      background-color: var(--neutral-translucent-10, #272d371a);
    }
  }

  &__grid-list {
    display: grid;
    gap: 0.5rem;
    justify-items: center;
    align-items: center;
  }

  &__popover-content {
    display: grid;
  }
  &__popover-content-divider {
    position: relative;
    &::after {
      content: "";
      display: block;
      position: absolute;
      left: 50%;
      top: 0;
      bottom: 0;
      transform: translateX(-50%);
      width: 0.063rem;
      background-color: var(--neutral-30, #bfc0c3);
    }
  }

  &__select {
    display: flex;
    align-items: center;
    background: var(--neutral-10, #eaeaeb);
    border-radius: 0.5rem;
    user-select: none;
    min-height: 3.25rem;
    padding-right: 0.75rem;
    border: solid 0.125rem transparent;

    &.is-selected {
      background-color: var(--neutral-0, #ffffff);
      border-color: var(--neutral-40, #a9abaf);
    }
  }
  &__select-info {
    width: 4.563rem;
    padding: 0.125rem 0 0.125rem 0.625rem;
  }
  &__select-chevron {
    width: 1.5rem;
    height: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transform: rotate(180deg);
    transition: transform 0.2s ease;

    &.is-opened {
      transform: rotate(360deg);
    }
  }

  &__color-square {
    width: 1.375rem;
    height: 1.375rem;
    border-radius: 0.5rem;
  }
}
</style>
